import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDebounce } from "use-debounce"
import { Box, CloseButton, Dialog, HStack, Input, Portal, Separator, Spinner, Stack } from "@chakra-ui/react"
import { CompanyInfoFractal, EsoDealFractal } from "@/types"
import { LegacyDealResult, legacySyncByStr, searchLegacyDeals } from "@/api"
import { useSearchedCompanies, useSearchedDeals } from "@/hooks"
import { Text } from "../ui"

interface Props {
  isOpen: boolean
  onClose: () => void
}

type FilterType = "all" | "companies" | "deals"
type LegacyState = "idle" | "searching" | "found" | "empty" | "error"

const PAGE_SIZE = 50

export const SearchCompaniesAndDealsModal = ({ isOpen, onClose }: Props) => {
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState("")
  const [debouncedSearch] = useDebounce(searchText, 300)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [filter, setFilter] = useState<FilterType>("all")

  // Legacy search state
  const [legacyState, setLegacyState] = useState<LegacyState>("idle")
  const [legacyResults, setLegacyResults] = useState<LegacyDealResult[]>([])
  const [syncingDealIdStr, setSyncingDealIdStr] = useState<string | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)

  const trimmedSearch = debouncedSearch.trim()
  const { data: companies, isLoading: isCompaniesLoading } = useSearchedCompanies(trimmedSearch)
  const { data: deals, isLoading: isDealsLoading } = useSearchedDeals(trimmedSearch)

  const isLoading =
    filter === "all" ? isCompaniesLoading || isDealsLoading
    : filter === "companies" ? isCompaniesLoading
    : isDealsLoading

  const filteredCompanies = useMemo(() => {
    if (filter === "deals") return []
    if (!companies || companies.length === 0) return []
    if (!searchText) return companies

    const search = searchText.toLowerCase().trim()
    if (search === debouncedSearch.toLowerCase().trim()) return companies

    return companies.filter((company) =>
      company.name.toLowerCase().includes(search)
    )
  }, [companies, searchText, debouncedSearch, filter])

  const filteredDeals = useMemo(() => {
    if (filter === "companies") return []
    if (!deals || deals.length === 0) return []
    return deals
  }, [deals, filter])


  type ResultItem =
    | { kind: "company"; data: CompanyInfoFractal }
    | { kind: "deal"; data: EsoDealFractal }

  const combinedResults = useMemo<ResultItem[]>(() => {
    const items: ResultItem[] = []
    filteredCompanies.forEach((c) => items.push({ kind: "company", data: c }))
    filteredDeals.forEach((d) => items.push({ kind: "deal", data: d }))
    return items
  }, [filteredCompanies, filteredDeals])

  const visibleResults = combinedResults.slice(0, visibleCount)

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [combinedResults.length])

  // Reset legacy search whenever the debounced search term changes
  useEffect(() => {
    setLegacyState("idle")
    setLegacyResults([])
    setSyncingDealIdStr(null)
    setSyncError(null)
  }, [trimmedSearch])

  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = loadMoreRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, combinedResults.length)
          )
        }
      },
      { threshold: 0.1, root: scrollContainerRef.current }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [combinedResults.length])

  useEffect(() => {
    if (!isOpen) {
      setSearchText("")
      setVisibleCount(PAGE_SIZE)
      setFilter("all")
      setLegacyState("idle")
      setLegacyResults([])
      setSyncingDealIdStr(null)
      setSyncError(null)
    }
  }, [isOpen])

  const handleSelectCompany = useCallback((company: CompanyInfoFractal) => {
    onClose()
    navigate(`/core/company/${company.id}`)
  }, [navigate, onClose])

  const handleSelectDeal = useCallback((deal: EsoDealFractal) => {
    onClose()
    navigate(`/core/deal/${deal.id}`)
  }, [navigate, onClose])

  const handleLegacySearch = useCallback(async () => {
    if (!trimmedSearch || trimmedSearch.length < 2) return
    setLegacyState("searching")
    setSyncError(null)
    try {
      const results = await searchLegacyDeals(trimmedSearch)
      setLegacyResults(results)
      setLegacyState(results.length > 0 ? "found" : "empty")
    } catch {
      setLegacyState("error")
    }
  }, [trimmedSearch])

  const handleLegacySync = useCallback(async (dealIdStr: string) => {
    setSyncingDealIdStr(dealIdStr)
    setSyncError(null)
    try {
      const deal = await legacySyncByStr(dealIdStr)
      onClose()
      navigate(`/core/deal/${deal.id}`)
    } catch {
      setSyncError(`Failed to sync ${dealIdStr}`)
      setSyncingDealIdStr(null)
    }
  }, [navigate, onClose])

  const filterOptions: { value: FilterType; label: string }[] = [
    { value: "all", label: "All" },
    { value: "companies", label: "Companies" },
    { value: "deals", label: "Deals" },
  ]

  const placeholderText =
    filter === "all" ? "Search companies and deals..."
    : filter === "companies" ? "Search companies by name..."
    : "Search deals by name, email, or company..."

  return (
    <Dialog.Root size="xl" open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="card.bg">
            <Dialog.Body borderRadius="10" p={0}>
              <Stack h="80vh" w="100%" gap={0}>
                <Box w="100%" p={5}>
                  <Input
                    variant="subtle"
                    bg="transparent"
                    fontSize="xl"
                    placeholder={placeholderText}
                    fontFamily="body"
                    focusRingColor="transparent"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    autoFocus
                  />
                </Box>
                <Separator borderColor="border.default" />
                <HStack flex={1} overflow="hidden">
                  {/* Filter panel */}
                  <Stack
                    w="150px"
                    minW="150px"
                    h="100%"
                    py={3}
                    px={2}
                    borderRight="1px solid"
                    borderColor="border.default"
                    gap={1}
                  >
                    {filterOptions.map((opt) => (
                      <Box
                        key={opt.value}
                        px={3}
                        py={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={filter === opt.value ? "hover.bg" : "transparent"}
                        fontWeight={filter === opt.value ? "semibold" : "normal"}
                        _hover={{ bg: "hover.bg" }}
                        onClick={() => setFilter(opt.value)}
                      >
                        <Text fontSize="sm">{opt.label}</Text>
                        {searchText && (
                          <Text fontSize="xs" color="text.muted">
                            {opt.value === "companies"}
                            {opt.value === "deals"}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </Stack>

                  {/* Results */}
                  <Stack
                    ref={scrollContainerRef}
                    flex={1}
                    h="100%"
                    overflowY="auto"
                    px={5}
                    py={3}
                    gap={1}
                  >
                    {/* Legacy search entry — always shown when there's a search term */}
                    {trimmedSearch.length >= 2 && (
                      <Box mb={2}>
                        {legacyState === "idle" && (
                          <HStack
                            px={4}
                            py={3}
                            cursor="pointer"
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor="border.default"
                            borderStyle="dashed"
                            _hover={{ bg: "hover.bg" }}
                            onClick={handleLegacySearch}
                          >
                            <Box flex={1}>
                              <Text fontSize="sm" fontWeight="medium">
                                Sync deals with "{trimmedSearch}"
                              </Text>
                              <Text fontSize="xs" color="text.muted">
                                Search legacy database and import matching deals
                              </Text>
                            </Box>
                          </HStack>
                        )}

                        {legacyState === "searching" && (
                          <HStack px={4} py={3} borderRadius="md" borderWidth="1px" borderColor="border.default" borderStyle="dashed" gap={2}>
                            <Spinner size="sm" />
                            <Text fontSize="sm" color="text.muted">Searching legacy database...</Text>
                          </HStack>
                        )}

                        {legacyState === "empty" && (
                          <Box px={4} py={3} borderRadius="md" borderWidth="1px" borderColor="border.default" borderStyle="dashed">
                            <Text fontSize="sm" color="text.muted">No matches found in legacy database for "{trimmedSearch}"</Text>
                          </Box>
                        )}

                        {legacyState === "error" && (
                          <Box px={4} py={3} borderRadius="md" borderWidth="1px" borderColor="border.default" borderStyle="dashed">
                            <Text fontSize="sm" color="red.500">Failed to search legacy database</Text>
                          </Box>
                        )}

                        {legacyState === "found" && (
                          <Stack gap={1} borderWidth="1px" borderColor="border.default" borderStyle="dashed" borderRadius="md" px={2} py={2}>
                            <Text fontSize="xs" color="text.muted" px={2} pt={1}>
                              Found {legacyResults.length} deal{legacyResults.length !== 1 ? "s" : ""} in legacy database
                            </Text>
                            {syncError && (
                              <Text fontSize="xs" color="red.500" px={2}>{syncError}</Text>
                            )}
                            {legacyResults.map((result) => (
                              <HStack
                                key={result.dealIdStr}
                                px={3}
                                py={2}
                                borderRadius="md"
                                _hover={{ bg: "hover.bg" }}
                                gap={2}
                              >
                                <Box flex={1}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {result.clientFirstName} {result.clientLastName}
                                  </Text>
                                  <Text fontSize="xs" color="text.muted">
                                    {result.companyName}
                                    {result.dealIdStr ? ` — ${result.dealIdStr}` : ""}
                                    {result.stageName ? ` — ${result.stageName}` : ""}
                                    {result.managerName ? ` (${result.managerName})` : ""}
                                  </Text>
                                </Box>
                                <Box
                                  as="button"
                                  px={3}
                                  py={1}
                                  borderRadius="md"
                                  bg="blue.500"
                                  color="white"
                                  fontSize="xs"
                                  fontWeight="medium"
                                  cursor={syncingDealIdStr === result.dealIdStr ? "not-allowed" : "pointer"}
                                  opacity={syncingDealIdStr !== null && syncingDealIdStr !== result.dealIdStr ? 0.5 : 1}
                                  _hover={{ bg: syncingDealIdStr ? undefined : "blue.600" }}
                                  onClick={() => {
                                    if (syncingDealIdStr === null) handleLegacySync(result.dealIdStr)
                                  }}
                                >
                                  {syncingDealIdStr === result.dealIdStr ? (
                                    <HStack gap={1}>
                                      <Spinner size="xs" />
                                      <span>Syncing...</span>
                                    </HStack>
                                  ) : (
                                    "Sync"
                                  )}
                                </Box>
                              </HStack>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    )}

                    {!searchText && (
                      <Text fontSize="sm" color="text.muted" py={4} textAlign="center">
                        Start typing to search...
                      </Text>
                    )}

                    {searchText && isLoading && (
                      <Text fontSize="sm" color="text.muted" py={4} textAlign="center">
                        Searching...
                      </Text>
                    )}

                    {searchText && !isLoading && combinedResults.length === 0 && (
                      <Text fontSize="sm" color="text.muted" py={4} textAlign="center">
                        No results found.
                      </Text>
                    )}

                    {visibleResults.map((item) => {
                      if (item.kind === "company") {
                        const company = item.data
                        return (
                          <HStack
                            key={`company-${company.id}`}
                            px={4}
                            py={3}
                            cursor="pointer"
                            borderRadius="md"
                            _hover={{ bg: "hover.bg" }}
                            onClick={() => handleSelectCompany(company)}
                          >
                            <Box flex={1}>
                              <Text fontWeight="medium">{company.name}</Text>
                              {company.website && (
                                <Text fontSize="xs" color="text.muted">{company.website}</Text>
                              )}
                            </Box>
                            <Text fontSize="xs" color="text.subtle" bg="hover.bg" px={2} py={0.5} borderRadius="sm">
                              Company
                            </Text>
                          </HStack>
                        )
                      }

                      const deal = item.data
                      return (
                        <HStack
                          key={`deal-${deal.id}`}
                          px={4}
                          py={3}
                          cursor="pointer"
                          borderRadius="md"
                          _hover={{ bg: "hover.bg" }}
                          onClick={() => handleSelectDeal(deal)}
                        >
                          <Box flex={1}>
                            <Text fontWeight="medium">
                              {deal.client.firstName} {deal.client.lastName}
                            </Text>
                            <Text fontSize="xs" color="text.muted">
                              {deal.company?.name}{deal.dealIdStr ? ` — ${deal.dealIdStr}` : ""}
                            </Text>
                          </Box>
                          <Text fontSize="xs" color="text.subtle" bg="row.blue" px={2} py={0.5} borderRadius="sm">
                            Deal
                          </Text>
                        </HStack>
                      )
                    })}

                    {visibleCount < combinedResults.length && (
                      <Box ref={loadMoreRef} height="40px" />
                    )}
                  </Stack>
                </HStack>
              </Stack>
            </Dialog.Body>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
